import { useMemo } from "react"
import { Box, Table } from "@chakra-ui/react"
import { Text } from "@/components"
import {
  CapTableNote,
  CompanyFinancials,
  CompanyFinancialsBalanceSheet,
  CompanyFinancialsCashFlowStatement,
  CompanyFinancialsIncomeStatement,
  FinancialPeriod,
} from "@/types"

interface Props {
  companyFinancials: CompanyFinancials | null
  capTableNote?: CapTableNote | null
  title?: string
}

type UnitScale = "actuals" | "thousands" | "millions"

type PeriodSummary = {
  period: FinancialPeriod
  arr: number | null
  arrGrowthRate: number | null
  revenue: number | null
  revenueGrowthRate: number | null
  grossMargin: number | null
  cash: number | null
  monthlyBurn: number | string | null
  monthsOfRunway: number | string | null
  revenueMultiple: number | string | null
}

const QUARTER_LABELS = new Set(["q1", "q2", "q3", "q4"])

const isQuarterLabel = (label: string) => QUARTER_LABELS.has(label.toLowerCase())

const formatValue = (value: string | number | null | undefined) => {
  if (value === null || value === undefined || value === "") return "\u2014"
  const num = Number(value)
  if (isNaN(num)) return String(value)
  if (num < 0) return `(${new Intl.NumberFormat("en-US").format(Math.abs(num))})`
  return new Intl.NumberFormat("en-US").format(num)
}

const formatPercent = (value: number | null | undefined) => {
  if (value === null || value === undefined || isNaN(value) || !isFinite(value)) return null
  return `${(value * 100).toFixed(1)}%`
}

const calculateMonthlyBurn = (
  cashFlowOperating: number | null,
  netIncome: number | null
): number | string | null => {
  if (cashFlowOperating === null) {
    if (netIncome === null) return null
    if (netIncome > 0) return "Profitable"
    return netIncome / 12
  }
  if (cashFlowOperating > 0) return "CF from Op Positive"
  return cashFlowOperating / 12
}

const calculateMonthsOfRunway = (cash: number | null, monthlyBurn: number | string | null): number | string | null => {
  if (cash === null || monthlyBurn === null) return null
  if (typeof monthlyBurn === "string") return "N/A"
  if (monthlyBurn >= 0) return "N/A"
  return Math.round((-1 * cash) / monthlyBurn)
}

const calculateRevenueMultiple = (
  companyFDValuation: number | null,
  revenue: number | null,
  unit: UnitScale
): number | string | null => {
  if (companyFDValuation === null || revenue === null || revenue === 0) return null
  const scaleMap: Record<UnitScale, number> = { actuals: 1, thousands: 1_000, millions: 1_000_000 }
  return (companyFDValuation / scaleMap[unit]) / revenue
}

const calculateCompanyFDValuation = (capTableNote: CapTableNote | null | undefined): number | null => {
  if (!capTableNote) return null
  const totalAuthShares = Number(capTableNote.totalAuthShares)
  const fmv = Number(capTableNote.fmv)
  const esoLrp = Number(capTableNote.esoLrp)
  if (isNaN(totalAuthShares) || (isNaN(fmv) && isNaN(esoLrp))) return null
  return totalAuthShares * Math.max(fmv || 0, esoLrp || 0)
}

export const KeyMetricsSummary = ({ companyFinancials, capTableNote, title = "Key Metrics Summary" }: Props) => {
  const allPeriods = useMemo(() => {
    if (!companyFinancials) return []

    const periodMap = new Map<number, FinancialPeriod>()

    const collectPeriods = (entries: { companyFinancialPeriod: FinancialPeriod }[]) => {
      entries.forEach((entry) => {
        if (!periodMap.has(entry.companyFinancialPeriod.id)) {
          periodMap.set(entry.companyFinancialPeriod.id, entry.companyFinancialPeriod)
        }
      })
    }

    collectPeriods(companyFinancials.companyFinancialsIncomeStatement)
    collectPeriods(companyFinancials.companyFinancialsBalanceSheet)
    collectPeriods(companyFinancials.companyFinancialsCashFlowStatement)
    collectPeriods(companyFinancials.companyFinancialsAdditionalMetrics)

    return Array.from(periodMap.values()).sort((a, b) => {
      if (a.year !== b.year) return a.year - b.year
      const order = ["q1", "q2", "q3", "q4"]
      const aIdx = order.indexOf(a.label.toLowerCase())
      const bIdx = order.indexOf(b.label.toLowerCase())
      if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx
      return a.label.localeCompare(b.label)
    })
  }, [companyFinancials])

  const financialSummary = useMemo(() => {
    if (!companyFinancials) return null

    const companyFDValuation = calculateCompanyFDValuation(capTableNote)
    const unitScale: UnitScale = companyFinancials.financialsPresentedIn?.toLowerCase().includes("thousand")
      ? "thousands"
      : companyFinancials.financialsPresentedIn?.toLowerCase().includes("million")
        ? "millions"
        : "actuals"

    const incomeByPeriodId = new Map<number, CompanyFinancialsIncomeStatement>()
    const balanceByPeriodId = new Map<number, CompanyFinancialsBalanceSheet>()
    const cashFlowByPeriodId = new Map<number, CompanyFinancialsCashFlowStatement>()

    companyFinancials.companyFinancialsIncomeStatement.forEach((e) => incomeByPeriodId.set(e.companyFinancialPeriod.id, e))
    companyFinancials.companyFinancialsBalanceSheet.forEach((e) => balanceByPeriodId.set(e.companyFinancialPeriod.id, e))
    companyFinancials.companyFinancialsCashFlowStatement.forEach((e) => cashFlowByPeriodId.set(e.companyFinancialPeriod.id, e))

    const sortedPeriods = [...allPeriods].sort((a, b) => {
      if (a.year !== b.year) return b.year - a.year
      const order = ["fy", "q4", "q3", "q2", "q1"]
      return order.indexOf(a.label.toLowerCase()) - order.indexOf(b.label.toLowerCase())
    })

    const periodHasData = (period: FinancialPeriod): boolean => {
      const incomeEntry = incomeByPeriodId.get(period.id)
      const balanceEntry = balanceByPeriodId.get(period.id)
      const cashFlowEntry = cashFlowByPeriodId.get(period.id)

      const hasIncome = incomeEntry && (
        (incomeEntry.arr && Number(incomeEntry.arr) !== 0) ||
        (incomeEntry.revenue && Number(incomeEntry.revenue) !== 0) ||
        (incomeEntry.grossProfit && Number(incomeEntry.grossProfit) !== 0) ||
        (incomeEntry.netIncome && Number(incomeEntry.netIncome) !== 0)
      )
      const hasBalance = balanceEntry && (balanceEntry.cash && Number(balanceEntry.cash) !== 0)
      const hasCashFlow = cashFlowEntry && (cashFlowEntry.cashFlowOperating && Number(cashFlowEntry.cashFlowOperating) !== 0)

      return !!(hasIncome || hasBalance || hasCashFlow)
    }

    const latestFY = sortedPeriods.find((p) => p.label.toLowerCase() === "fy" && periodHasData(p))
    const latestQuarter = sortedPeriods.find((p) => isQuarterLabel(p.label) && periodHasData(p))

    const incomeByLabelYear = new Map<string, CompanyFinancialsIncomeStatement>()
    companyFinancials.companyFinancialsIncomeStatement.forEach((e) => {
      const key = `${e.companyFinancialPeriod.label.toLowerCase()}-${e.companyFinancialPeriod.year}`
      incomeByLabelYear.set(key, e)
    })

    const calculatePeriodSummary = (period: FinancialPeriod): PeriodSummary => {
      const incomeEntry = incomeByPeriodId.get(period.id)
      const balanceEntry = balanceByPeriodId.get(period.id)
      const cashFlowEntry = cashFlowByPeriodId.get(period.id)

      const arr = incomeEntry?.arr ? Number(incomeEntry.arr) : null
      const revenue = incomeEntry?.revenue ? Number(incomeEntry.revenue) : null
      const grossProfit = incomeEntry?.grossProfit ? Number(incomeEntry.grossProfit) : null
      const netIncome = incomeEntry?.netIncome ? Number(incomeEntry.netIncome) : null
      const cash = balanceEntry?.cash ? Number(balanceEntry.cash) : null
      const cashFlowOperating = cashFlowEntry?.cashFlowOperating ? Number(cashFlowEntry.cashFlowOperating) : null

      const grossMargin = revenue && grossProfit !== null ? grossProfit / revenue : null

      const prevYearKey = `${period.label.toLowerCase()}-${period.year - 1}`
      const prevIncomeEntry = incomeByLabelYear.get(prevYearKey)
      const prevArr = prevIncomeEntry?.arr ? Number(prevIncomeEntry.arr) : null
      const prevRevenue = prevIncomeEntry?.revenue ? Number(prevIncomeEntry.revenue) : null

      const arrGrowthRate = arr !== null && prevArr !== null && prevArr !== 0
        ? (arr - prevArr) / Math.abs(prevArr)
        : null

      const revenueGrowthRate = revenue !== null && prevRevenue !== null && prevRevenue !== 0
        ? (revenue - prevRevenue) / Math.abs(prevRevenue)
        : null

      const monthlyBurn = calculateMonthlyBurn(cashFlowOperating, netIncome)
      const monthsOfRunway = calculateMonthsOfRunway(cash, monthlyBurn)
      const revenueMultiple = calculateRevenueMultiple(companyFDValuation, revenue, unitScale)

      return {
        period, arr, arrGrowthRate, revenue, revenueGrowthRate, grossMargin,
        cash, monthlyBurn, monthsOfRunway, revenueMultiple,
      }
    }

    return {
      fy: latestFY ? calculatePeriodSummary(latestFY) : null,
      quarter: latestQuarter ? calculatePeriodSummary(latestQuarter) : null,
    }
  }, [companyFinancials, allPeriods, capTableNote])

  if (!financialSummary || (!financialSummary.fy && !financialSummary.quarter)) {
    return null
  }

  const renderMetricRow = (label: string, getValue: (s: PeriodSummary) => React.ReactNode) => (
    <Table.Row>
      <Table.Cell fontSize="xs">{label}</Table.Cell>
      {financialSummary.fy && <Table.Cell textAlign="right" fontSize="xs">{getValue(financialSummary.fy)}</Table.Cell>}
      {financialSummary.quarter && <Table.Cell textAlign="right" fontSize="xs">{getValue(financialSummary.quarter)}</Table.Cell>}
    </Table.Row>
  )

  const renderGrowthCell = (rate: number | null) => {
    const color = rate !== null ? (rate > 0 ? "green.600" : rate < 0 ? "red.600" : undefined) : undefined
    return <span style={{ color }}>{formatPercent(rate) || "\u2014"}</span>
  }

  const renderBurnCell = (burn: number | string | null) => {
    if (typeof burn === "string") return burn
    if (burn !== null) return formatValue(Math.round(burn))
    return "\u2014"
  }

  const renderRunwayCell = (runway: number | string | null) => {
    if (typeof runway === "string") return runway
    if (runway !== null) return `${Math.round(runway)}`
    return "\u2014"
  }

  const renderMultipleCell = (multiple: number | string | null) => {
    if (typeof multiple === "number") return `${multiple.toFixed(1)}x`
    return multiple || "\u2014"
  }

  return (
    <Box w="100%" p={5} border="1px" borderColor="gray.200" borderRadius={10} boxShadow="sm" bg="card.bg">
      <Text fontSize="lg" fontWeight="bold" mb={1}>
        {title}
      </Text>
      {companyFinancials?.financialsPresentedIn && (
        <Text fontSize="xs" color="text.muted">
          Presented in {companyFinancials.financialsPresentedIn}
        </Text>
      )}
      <Table.Root size="sm" striped>
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeader w="150px" />
            {financialSummary.fy && (
              <Table.ColumnHeader textAlign="right" fontSize="xs" fontWeight="bold">
                FY {financialSummary.fy.period.year}
              </Table.ColumnHeader>
            )}
            {financialSummary.quarter && (
              <Table.ColumnHeader textAlign="right" fontSize="xs" fontWeight="bold">
                {financialSummary.quarter.period.label.toUpperCase()} {financialSummary.quarter.period.year}
              </Table.ColumnHeader>
            )}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {renderMetricRow("ARR", (s) => formatValue(s.arr))}
          {renderMetricRow("ARR Growth Rate", (s) => renderGrowthCell(s.arrGrowthRate))}
          {renderMetricRow("Revenue", (s) => formatValue(s.revenue))}
          {renderMetricRow("Revenue Growth Rate", (s) => renderGrowthCell(s.revenueGrowthRate))}
          {renderMetricRow("Gross Margin", (s) => formatPercent(s.grossMargin) || "\u2014")}
          {renderMetricRow("Cash", (s) => formatValue(s.cash))}
          {renderMetricRow("Monthly Burn", (s) => renderBurnCell(s.monthlyBurn))}
          {renderMetricRow("Months of Runway", (s) => renderRunwayCell(s.monthsOfRunway))}
          {renderMetricRow("Revenue Multiple", (s) => renderMultipleCell(s.revenueMultiple))}
        </Table.Body>
      </Table.Root>
    </Box>
  )
}
